'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Button } from '@govinity/ui/components/ui/button';
import { NavigationMenu, NavigationMenuList } from '@govinity/ui/components/ui/navigation-menu';
import { MobileMenu } from '@govinity/ui/components/blocks/navbar/mobile-menu';
import { ThemeToggle } from '@govinity/ui/contexts/theme-toggle';
import { HelpCenterNavButton } from '@govinity/ui/components/ui-custom/help-center-nav-button';
import { UserNav } from '@govinity/ui/components/blocks/navbar/user-nav';
import { Logo } from '@govinity/ui/components/blocks/navbar/logo';
import { LoginButton } from '@govinity/ui/components/blocks/navbar/login-button';
import type { AuthProps, LogoProps, MenuItem } from '@govinity/ui/components/blocks/navbar/types';
import { Home } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PublicMenuItem } from './public-menu-item';

const HELP_CENTER_URL = 'https://help.govinity.com';

const backgroundClass = 'from-blue-800 via-indigo-800 to-purple-800';

const liveBackgroundClass =
  'from-green-700 via-emerald-700 to-green-800 dark:from-green-800 dark:via-emerald-900 dark:to-green-900';

const recessBackgroundClass =
  'from-orange-700 via-amber-700 to-orange-800 dark:from-orange-800 dark:via-amber-900 dark:to-orange-900';

const mobileMenuClassName = cn(backgroundClass, 'dark bg-linear-to-r text-white dark:text-white');

/**
 * The live meeting the member is currently inside, or `null`. When set, the nav swaps its brand
 * gradient for a green "live" (or orange "recess") treatment and shows a persistent, non-scrolling
 * indicator.
 */
type LiveMeeting = { title: string; isRecessed: boolean } | null;

function getHeaderBackgroundClass(liveMeeting: LiveMeeting): string {
  if (!liveMeeting) {
    return backgroundClass;
  }

  return liveMeeting.isRecessed ? recessBackgroundClass : liveBackgroundClass;
}

/**
 * Persistent indicator shown in the nav while a member is inside a live meeting. The state is
 * conveyed by the "LIVE"/"RECESS" text and meeting title (not color alone) for WCAG; the dot is
 * decorative — it only animates when live and the user hasn't requested reduced motion, and holds
 * steady during recess.
 */
function LiveMeetingIndicator({ title, isRecessed }: { title: string; isRecessed: boolean }) {
  const t = useTranslations('PublicNavbar');
  return (
    <output className="inline-flex max-w-full min-w-0 items-center gap-2 overflow-hidden rounded-full bg-white/20 px-3 py-1 text-sm text-white">
      <span
        className={cn(
          'inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-white',
          !isRecessed && 'motion-safe:animate-pulse'
        )}
        aria-hidden="true"
      />
      <span className="shrink-0 font-medium tracking-wide">
        {isRecessed ? t('recessStatus') : t('liveStatus')}
      </span>
      <span aria-hidden="true" className="sr-only shrink-0 truncate opacity-70 lg:not-sr-only">
        ·
      </span>
      <span className="sr-only line-clamp-1 lg:not-sr-only">{title}</span>
    </output>
  );
}

interface PublicNavbarProps {
  logo: LogoProps;
  menu: MenuItem[];
  auth: AuthProps;
  userAvatarSlot?: React.ReactNode;
  showThemeToggle?: boolean;
  hideLogo?: boolean;
  liveMeeting?: LiveMeeting;
}

export const PublicNavbar = ({
  logo,
  menu,
  auth,
  userAvatarSlot,
  showThemeToggle = false,
  hideLogo = false,
  liveMeeting = null,
}: PublicNavbarProps) => {
  const t = useTranslations('PublicNavbar');
  const isRecessed = liveMeeting?.isRecessed ?? false;
  const liveIndicator = liveMeeting ? (
    <LiveMeetingIndicator title={liveMeeting.title} isRecessed={isRecessed} />
  ) : null;

  return (
    <header
      className={cn(
        'border-b border-white/20 bg-linear-to-r px-4 py-3 lg:py-0 dark:border-white/10',
        getHeaderBackgroundClass(liveMeeting)
      )}
      role="banner"
    >
      <div className="container px-0">
        <nav
          aria-label={t('mainNavigationAriaLabel')}
          role="navigation"
          className={cn('relative hidden min-h-[56px] justify-between lg:flex')}
        >
          <div className="flex min-w-0 flex-1 items-center gap-4">
            {!hideLogo && <Logo {...logo} />}

            {/* Left slot: LIVE indicator while in a live meeting, otherwise a spacer */}
            {liveIndicator && (
              <div className="flex min-w-0 flex-1 items-center">{liveIndicator}</div>
            )}

            {/* Center navigation */}
            <div className="flex justify-center">
              <NavigationMenu>
                <NavigationMenuList className="gap-2 lg:gap-4 xl:gap-6 2xl:gap-12">
                  {menu.map((item) => (
                    <PublicMenuItem key={item.key || item.title} item={item} />
                  ))}
                </NavigationMenuList>
              </NavigationMenu>
            </div>
          </div>

          {/* Right controls */}
          <div className="flex items-center gap-2">
            {showThemeToggle && (
              <div className="[&_button]:text-white [&_button]:hover:bg-white/10 [&_button]:hover:text-white/80">
                <ThemeToggle />
              </div>
            )}

            <div className="[&_a]:text-white [&_a]:hover:bg-white/10 [&_a]:hover:text-white/80">
              <HelpCenterNavButton href={HELP_CENTER_URL} />
            </div>

            {auth.showAccount && (
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10 hover:text-white/80"
              >
                <Link href="/account">
                  <Home className="mr-2 h-4 w-4" />
                  {t('accountLink')}
                </Link>
              </Button>
            )}

            {auth.showUserNav && (
              <div className="[&_button]:text-white [&_button]:hover:bg-white/10 [&_button]:hover:text-white/80">
                <UserNav {...auth} userAvatarSlot={userAvatarSlot} />
              </div>
            )}

            {auth.login && <LoginButton loginUrl={auth.login.url}>{auth.login.text}</LoginButton>}

            {auth.signup && (
              <LoginButton loginUrl={auth.signup.url} isSignup variant="default">
                {auth.signup.text}
              </LoginButton>
            )}
          </div>
        </nav>

        {/* Mobile Menu */}
        <MobileMenu
          logo={logo}
          helpCenterUrl={HELP_CENTER_URL}
          hideLogo={hideLogo}
          menu={menu}
          auth={auth}
          userAvatarSlot={userAvatarSlot}
          showThemeToggle={showThemeToggle}
          startSlot={liveIndicator}
          className={mobileMenuClassName}
        />
      </div>
    </header>
  );
};
